import { ClickAwayListener } from "@mui/material";
import { useAtomValue } from "jotai";
import type { Dispatch, FunctionComponent, SetStateAction } from "react";
import { graphql, useFragment } from "react-relay";

import { useLoginDialogStateHelper } from "~/components/login-dialog/hooks";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { config } from "~/data/configs";
import { accountAtom } from "~/lib/account";
import type { toolTipArticleSpeechWidgetToolTipArticle$key } from "~/queries/__generated__/toolTipArticleSpeechWidgetToolTipArticle.graphql";

import {
  ClickAwayListenerWrapper,
  Link,
  LogInText,
  StyledClose,
  StyledTooltip,
  Text,
  ToolTipMessage,
} from "./styles";

type Props = {
  children: JSX.Element;
  isTooltipActive: boolean;
  reference: toolTipArticleSpeechWidgetToolTipArticle$key;
  setIsTooltipActive: Dispatch<SetStateAction<boolean>>;
};

export const ArticleSpeechWidgetToolTip: FunctionComponent<Props> = ({
  children,
  isTooltipActive,
  reference,
  setIsTooltipActive,
}) => {
  const article = useFragment(
    graphql`
      fragment toolTipArticleSpeechWidgetToolTipArticle on Article {
        entityId
      }
    `,
    reference,
  );

  const { isLoggedIn } = useAtomValue(accountAtom);
  const { openLoginDialog } = useLoginDialogStateHelper();

  const handleLoginClick = () => {
    sendGATracking({
      action: "Subscription Entry Point/TTS/Sign in/Click",
      category: "Subscription Entry Point",
      label: window.location.href,
    });

    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        article_id: article.entityId,
        cta_type: "sign in",
        trigger_point: "text to speech",
      },
      subcategory: "entry_point",
    });

    openLoginDialog({
      description: "Sign in to enjoy our audio option, only for subscribers.",
      destination: window.location.href,
      ga4CustomParameter: {
        trigger_point: "text to speech",
      },
      title: "Listen to this article",
    });
  };

  const handleSubscriptionClickEvent = () => {
    sendGATracking({
      action: "Subscription Entry Point/TTS/Subscribe/Click",
      category: "Subscription Entry Point",
      label: window.location.href,
    });

    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        article_id: article.entityId,
        cta_type: "subscribe",
        trigger_point: "text to speech",
      },
      subcategory: "entry_point",
    });
  };

  return (
    <ClickAwayListener onClickAway={() => setIsTooltipActive(false)}>
      <ClickAwayListenerWrapper>
        <StyledTooltip
          open={isTooltipActive}
          placement="top"
          title={
            <ToolTipMessage>
              <Text>
                Our audio option is only available for subscribers.{" "}
                {!isLoggedIn && (
                  <>
                    <LogInText onClick={() => handleLoginClick()}>LOG IN</LogInText> or{" "}
                  </>
                )}
                <Link
                  href={`${config.rosetta.subscribeHost}`}
                  onClick={() => handleSubscriptionClickEvent()}
                >
                  SUBSCRIBE NOW
                </Link>
              </Text>
              <StyledClose
                onClick={event => {
                  setIsTooltipActive(false);
                  event?.stopPropagation();
                }}
              />
            </ToolTipMessage>
          }
          arrow
        >
          {children}
        </StyledTooltip>
      </ClickAwayListenerWrapper>
    </ClickAwayListener>
  );
};

ArticleSpeechWidgetToolTip.displayName = "ArticleSpeechWidgetToolTip";
